
import { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { PricingSection } from "@/components/pricing/PricingSection";
import { BusinessTypeSelect } from "@/components/onboarding/BusinessTypeSelect";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/card";
import { CheckCircle, Clock } from "lucide-react";
import { TrialBanner } from "@/components/subscription/TrialBanner";

const Pricing = () => {
  const [hasCompletedSetup, setHasCompletedSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userBusinessType, setUserBusinessType] = useState<string | null>(null);
  const [trialStatus, setTrialStatus] = useState<'none' | 'active' | 'expired'>('none');
  const [trialEndDate, setTrialEndDate] = useState<string | null>(null);

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setLoading(false);
        return;
      }

      // Check user's business type from profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('business_size')
        .eq('id', user.id)
        .single();

      if (profile?.business_size) {
        setUserBusinessType(profile.business_size);
      }

      // Check if user has completed setup fee payment
      const { data: setupPayments } = await supabase
        .from('payments')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'successful')
        .eq('payment_type', 'one_time')
        .not('business_type', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1);

      const hasSetup = setupPayments && setupPayments.length > 0;
      setHasCompletedSetup(hasSetup);

      if (hasSetup) {
        const setupPayment = setupPayments[0];
        
        // Check if trial is active or expired
        if (setupPayment.trial_end_date) {
          const trialEnd = new Date(setupPayment.trial_end_date);
          const now = new Date();
          
          setTrialEndDate(setupPayment.trial_end_date);
          
          if (now < trialEnd) {
            setTrialStatus('active');
          } else {
            // Check if user has active subscription
            const { data: subscriptions } = await supabase
              .from('payments')
              .select('*')
              .eq('user_id', user.id)
              .eq('payment_type', 'subscription')
              .eq('status', 'successful')
              .eq('subscription_status', 'active');

            if (!subscriptions || subscriptions.length === 0) {
              setTrialStatus('expired');
            }
          }
        }
      }
    } catch (error) {
      console.error("Error checking setup status:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </MainLayout>
    );
  }

  const renderContent = () => {
    if (!hasCompletedSetup) {
      return (
        <div className="space-y-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Welcome to NextWave AI</h1>
            <p className="text-xl text-muted-foreground mb-8">
              Complete your one-time setup to unlock monthly subscription plans
            </p>
          </div>
          <BusinessTypeSelect />
        </div>
      );
    }

    if (trialStatus === 'active' && trialEndDate) {
      return (
        <div className="space-y-8">
          <TrialBanner 
            trialEndDate={trialEndDate} 
            onUpgrade={() => setTrialStatus('expired')} 
          />
          <Card className="p-6 bg-blue-50 border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-6 h-6 text-blue-600" />
              <h3 className="text-lg font-semibold text-blue-800">Trial Period Active</h3>
            </div>
            <p className="text-blue-700 mb-4">
              Your 7-day free trial is active! Explore all features and choose your subscription plan before it expires.
            </p>
          </Card>
          <PricingSection businessType={userBusinessType} />
        </div>
      );
    }

    return (
      <div className="space-y-8">
        <Card className="p-6 bg-green-50 border-green-200">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <h3 className="text-lg font-semibold text-green-800">
              {trialStatus === 'expired' ? 'Choose Your Plan' : 'Setup Complete!'}
            </h3>
          </div>
          <p className="text-green-700">
            {trialStatus === 'expired' 
              ? 'Your trial has ended. Select a subscription plan to continue using NextWave AI.'
              : 'Your initial setup is complete. Choose your monthly subscription plan below.'
            }
          </p>
        </Card>
        <PricingSection businessType={userBusinessType} />
      </div>
    );
  };

  return (
    <MainLayout>
      {renderContent()}
    </MainLayout>
  );
};

export default Pricing;
